import { useState } from "react";
import { Button, Tooltip } from "@fluentui/react-components";
import { ReceiptMoneyRegular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import api from "service/api";
import useNotificationHandler from "hooks/useNotificationHandler";
import {
  ExportInvoicesDialog,
  ExportInvoicesRange,
  toIssuedFromParam,
  toIssuedToParam,
} from "./ExportInvoicesDialog";

/** GET invoices/export?format=xlsx response: the workbook as base64 (null when nothing was found) and its file name */
type ExcelContentResponse = {
  Content: string | null;
  Name: string;
};

const EXCEL_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const downloadBase64File = (base64: string, name: string, contentType: string) => {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: contentType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * "Export invoices" action of the offers list: asks for the issue date range (the last seven days by default),
 * then downloads every MTPL, CASCO and group invoice issued in it as an Excel file
 * built by the backend (GET invoices/export) for import into the Premium Collection Tool.
 */
export const ExportInvoicesButton = () => {
  const { t } = useTranslation();
  const { handleAddNotification } = useNotificationHandler();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const exportInvoices = async ({ issuedFrom, issuedTo }: ExportInvoicesRange) => {
    setIsDialogOpen(false);
    setIsExporting(true);
    try {
      const response = await api.get("/invoices/export", {
        params: {
          issuedFrom: toIssuedFromParam(issuedFrom),
          issuedTo: toIssuedToParam(issuedTo),
          format: "xlsx",
        },
      });
      const file: ExcelContentResponse | undefined = response?.data;
      if (!file?.Content) {
        handleAddNotification({
          id: "export-invoices-none",
          type: "info",
          text: t(
            "qtg.export.invoices.none",
            "There are no invoices issued in the chosen period.",
          ),
        });
        return;
      }
      downloadBase64File(file.Content, file.Name, EXCEL_CONTENT_TYPE);
    } catch (err: any) {
      handleAddNotification({
        id: "export-invoices-error",
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          t("qtg.export.invoices.failed", "The invoice export failed."),
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Tooltip
        content={t("qtg.export.invoices.title", "Export invoices")}
        relationship="label"
      >
        <Button
          data-help="offers-export-invoices"
          appearance="secondary"
          icon={<ReceiptMoneyRegular />}
          disabled={isExporting}
          onClick={() => setIsDialogOpen(true)}
        />
      </Tooltip>
      <ExportInvoicesDialog
        open={isDialogOpen}
        onCancel={() => setIsDialogOpen(false)}
        onConfirm={exportInvoices}
      />
    </>
  );
};
